import React, {
  useEffect,
  useState,
  useRef,
  useCallback,
  useMemo,
  useReducer,
} from "react";
import {
  Box,
  Paper,
  Typography,
  Grid,
  TextField,
  Button,
  Switch,
  FormControlLabel,
  MenuItem,
  Select,
  FormControl,
  Collapse,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Card,
  CardContent,
  Fade,
  Chip,
} from "@mui/material";
import {
  ExpandMore,
  ExpandLess,
  Edit,
  Delete,
  AccessTime,
  EventAvailable,
  Person,
  Schedule,
  CheckCircle,
  RadioButtonUnchecked,
  Circle,
  Save,
  Cancel,
  Today,
} from "@mui/icons-material";
import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import interactionPlugin from "@fullcalendar/interaction";
import api from "../api/api";
import CustomSnackbar from "../components/CustomSnackbar";
import LinearProgress from "@mui/material/LinearProgress";
import en_ja from "../en-ja-map";

const timeUtils = {
  formatDate: (date) => {
    const d = new Date(date);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(
      2,
      "0"
    )}-${String(d.getDate()).padStart(2, "0")}`;
  },
  timeToMinutes: (timeStr) => {
    const [hours, minutes] = timeStr.split(":").map(Number);
    return hours * 60 + minutes;
  },
  minutesToTime: (minutes) => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${String(hours).padStart(2, "0")}:${String(mins).padStart(2, "0")}`;
  },
  isPastDate: (dateString) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const checkDate = new Date(dateString);
    checkDate.setHours(0, 0, 0, 0);
    return checkDate < today;
  },
  isToday: (dateString) => {
    const today = new Date();
    return dateString === timeUtils.formatDate(today);
  },
  isFutureDate: (dateString) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const checkDate = new Date(dateString);
    checkDate.setHours(0, 0, 0, 0);
    return checkDate > today;
  },
  validateTodaySlot: (timeStr, selectedDate) => {
    if (!timeUtils.isToday(selectedDate)) return { valid: true };
    const now = new Date();
    const currentMinutes = now.getHours() * 60 + now.getMinutes();
    const slotMinutes = timeUtils.timeToMinutes(timeStr);
    const requiredMinutes = currentMinutes + 2;
    if (slotMinutes < requiredMinutes) {
      const minTime = timeUtils.minutesToTime(requiredMinutes);
      return {
        valid: false,
        message: `${en_ja["For today's slots, minimum time must be"]} ${minTime}`,
      };
    }
    return { valid: true };
  },
  canEditSlot: (slot, selectedDate) => {
    if (timeUtils.isPastDate(selectedDate)) return false;
    if (timeUtils.isToday(selectedDate)) {
      const now = new Date();
      const currentMinutes = now.getHours() * 60 + now.getMinutes();
      const slotMinutes = timeUtils.timeToMinutes(slot.start_time);
      return slotMinutes > currentMinutes;
    }
    return true;
  },
};

const slotReducer = (state, action) => {
  switch (action.type) {
    case "SET_LOADING":
      return {
        ...state,
        loading: { ...state.loading, [action.section]: action.value },
      };
    case "SET_SLOTS":
      return {
        ...state,
        slots: { ...state.slots, [action.section]: action.data },
      };
    case "SET_NO_SLOTS":
      return {
        ...state,
        noSlots: { ...state.noSlots, [action.section]: action.value },
      };
    case "SET_SELECTED_DATE":
      return { ...state, selectedDate: action.date };
    case "SET_DATES":
      return {
        ...state,
        availableSlotsDate: action.availableDate,
        selectedHistoryDate: action.historyDate,
      };
    case "RESET_STATE":
      return { ...action.initialState };
    default:
      return state;
  }
};

const formReducer = (state, action) => {
  switch (action.type) {
    case "SET_FIELD":
      const newData = { ...state.data, [action.field]: action.value };
      if (action.field === "fromTime" || action.field === "slotDuration") {
        const fromTime =
          action.field === "fromTime" ? action.value : newData.fromTime;
        const duration =
          parseInt(
            action.field === "slotDuration"
              ? action.value
              : newData.slotDuration
          ) || 30;
        const fromMinutes = timeUtils.timeToMinutes(fromTime);
        const toMinutes = fromMinutes + duration;
        newData.toTime = timeUtils.minutesToTime(
          toMinutes >= 1440 ? toMinutes - 1440 : toMinutes
        );
      }
      return {
        ...state,
        data: newData,
        errors: { ...state.errors, [action.field]: "" },
      };
    case "SET_ERRORS":
      return { ...state, errors: action.errors };
    case "RESET_FORM":
      return { ...action.initialState };
    case "SET_ALL_DATA":
      return { ...state, data: action.data, errors: {} };
    default:
      return state;
  }
};

const AppointmentSlots = () => {
  const user = JSON.parse(localStorage.getItem("user"));
  const todayDate = useMemo(() => timeUtils.formatDate(new Date()), []);
  const calendarRef = useRef(null);
  const historyRef = useRef(null);

  const initialSlotState = {
    selectedDate: todayDate,
    availableSlotsDate: todayDate,
    selectedHistoryDate: "",
    slots: { available: [], history: [] },
    loading: { available: false, history: false, create: false, edit: false },
    noSlots: { available: false, history: false },
  };

  const initialFormState = {
    data: {
      fromTime: "10:00",
      toTime: "10:30",
      slotCount: "04",
      slotDuration: "30",
      durationUnit: en_ja["Mins"],
      autoApprove: true,
    },
    errors: {},
  };

  const initialEditFormState = {
    data: {
      fromTime: "10:00",
      toTime: "10:30",
      slotCount: "",
      slotDuration: "30",
      durationUnit: "Mins",
      autoApprove: true,
    },
    errors: {},
  };

  const [slotState, slotDispatch] = useReducer(slotReducer, initialSlotState);
  const [formState, formDispatch] = useReducer(formReducer, initialFormState);
  const [editFormState, editFormDispatch] = useReducer(
    formReducer,
    initialEditFormState
  );

  const [highlightHistory, setHighlightHistory] = useState(false);
  const [historyExpanded, setHistoryExpanded] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarType, setSnackbarType] = useState("success");
  const [selectedCalendarDate, setSelectedCalendarDate] = useState(null);

  const formatDate = (dateInput) => {
    if (!dateInput) return "";
    const date = new Date(dateInput);

    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0"); // months are 0-based
    const day = String(date.getDate()).padStart(2, "0");

    return `${year}/${month}/${day}`;
  }

  const showSnackbar = useCallback((message, type = "success") => {
    setSnackbarMessage(message);
    setSnackbarType(type);
    setSnackbarOpen(true);
  }, []);

  const isSameDate = (date1, date2) => {
    if (!date1 || !date2) return false;
    return date1 === date2; // Since you're using string dates (YYYY-MM-DD format)
  }

  const fetchSlots = useCallback(
    async (date, isHistory = false) => {
      const section = isHistory ? "history" : "available";
      slotDispatch({ type: "SET_LOADING", section, value: true });
      slotDispatch({ type: "SET_NO_SLOTS", section, value: false });

      try {
        const response = await api.post("v1/clinic/slots/history", {
          date,
          clinicId: user.id,
        });
        slotDispatch({
          type: "SET_SLOTS",
          section,
          data: response.data.data || [],
        });
      } catch (err) {
        if (err.response?.status === 404) {
          slotDispatch({ type: "SET_SLOTS", section, data: [] });
          slotDispatch({ type: "SET_NO_SLOTS", section, value: true });
        } else {
          showSnackbar(`Error fetching ${section} slots`, "error");
        }
      } finally {
        slotDispatch({ type: "SET_LOADING", section, value: false });
      }
    },
    [user.id, showSnackbar]
  );

  const handleDateClick = useCallback(
    (arg) => {
      const clickedDate = timeUtils.formatDate(arg.date);
      slotDispatch({ type: "SET_SELECTED_DATE", date: clickedDate });

      setSelectedCalendarDate(clickedDate);

      if (timeUtils.isPastDate(clickedDate)) {
        setHighlightHistory(true);
        setHistoryExpanded(true);
        slotDispatch({
          type: "SET_DATES",
          availableDate: slotState.availableSlotsDate,
          historyDate: clickedDate,
        });
        fetchSlots(clickedDate, true);
        setTimeout(() => {
          historyRef.current?.scrollIntoView({
            behavior: "smooth",
            block: "center",
          });
        }, 150);
      } else {
        slotDispatch({
          type: "SET_DATES",
          availableDate: clickedDate,
          historyDate: slotState.selectedHistoryDate,
        });
        fetchSlots(clickedDate, false);
      }
    },
    [fetchSlots, slotState.availableSlotsDate, slotState.selectedHistoryDate]
  );

  useEffect(() => {
    const timer = highlightHistory
      ? setTimeout(() => setHighlightHistory(false), 2500)
      : null;
    return () => timer && clearTimeout(timer);
  }, [highlightHistory]);

  useEffect(() => {
    fetchSlots(todayDate, false);
  }, [todayDate, fetchSlots]);

  useEffect(() => {
    if (calendarRef.current) {
      // Force FullCalendar to re-render when selectedDate changes
      const calendarApi = calendarRef.current.getApi();
      calendarApi.render();
    }
  }, [slotState.selectedDate]);

  const validateForm = useCallback(
    (data, selectedDate) => {
      const errors = {};
      if (!data.fromTime) errors.fromTime = en_ja["From time is required"];
      if (!data.toTime) errors.toTime = en_ja["To time is required"];
      if (!data.slotCount || parseInt(data.slotCount) <= 0)
        errors.slotCount = en_ja["Slot count must be greater than 0"];
      if (!data.slotDuration || parseInt(data.slotDuration) <= 0)
        errors.slotDuration = en_ja["Slot duration must be greater than 0"];

      const fromMinutes = timeUtils.timeToMinutes(data.fromTime);
      const toMinutes = timeUtils.timeToMinutes(data.toTime);
      if (toMinutes <= fromMinutes)
        errors.toTime = en_ja["To time must be after from time"];

      if (timeUtils.isPastDate(selectedDate)) {
        const errorMessage = en_ja["Cannot create slots for past dates"];
        showSnackbar(errorMessage, "error");
        return { valid: false, errors };
      }

      const todayValidation = timeUtils.validateTodaySlot(
        data.fromTime,
        selectedDate
      );
      if (!todayValidation.valid) errors.fromTime = todayValidation.message;

      return { valid: Object.keys(errors).length === 0, errors };
    },
    [showSnackbar]
  );

  const handleFormChange = useCallback((field, value) => {
    formDispatch({ type: "SET_FIELD", field, value });
  }, []);

  const handleEditFormChange = useCallback((field, value) => {
    editFormDispatch({ type: "SET_FIELD", field, value });
  }, []);

  const handleSave = useCallback(async () => {
    const validation = validateForm(formState.data, slotState.selectedDate);
    if (!validation.valid) {
      formDispatch({ type: "SET_ERRORS", errors: validation.errors });
      if (Object.keys(validation.errors).length > 0) {
        const errorMessage = en_ja["Please fix the errors before submitting"];
        showSnackbar(errorMessage, "error");
      }
      return;
    }

    slotDispatch({ type: "SET_LOADING", section: "create", value: true });

    try {
      const apiData = {
        clinic_id: user.id,
        selectedDate: slotState.selectedDate,
        start_time: formState.data.fromTime,
        end_time: formState.data.toTime,
        total_count: formState.data.slotCount,
        slotDuration: formState.data.slotDuration,
        is_autoapproved: formState.data.autoApprove,
      };

      const response = await api.post("/v1/clinic/create/slots", apiData);
      showSnackbar(response.data.message);
      await fetchSlots(
        timeUtils.isPastDate(slotState.selectedDate)
          ? todayDate
          : slotState.selectedDate,
        false
      );
    } catch (err) {
      const errorMessage = en_ja["Error could not create slots!"];
      showSnackbar(errorMessage, "error");
    } finally {
      slotDispatch({ type: "SET_LOADING", section: "create", value: false });
    }
  }, [
    formState.data,
    slotState.selectedDate,
    validateForm,
    user.id,
    showSnackbar,
    fetchSlots,
    todayDate,
  ]);

  const handleSlotClick = useCallback(
    (slot) => {
      if (!timeUtils.canEditSlot(slot, slotState.availableSlotsDate)) return;

      const durationMinutes = parseInt(slot.slot_duration || "30");
      const startMinutes = timeUtils.timeToMinutes(slot.start_time);
      const endMinutes = startMinutes + durationMinutes;
      const endTime24h = timeUtils.minutesToTime(
        endMinutes >= 1440 ? endMinutes - 1440 : endMinutes
      );

      editFormDispatch({
        type: "SET_ALL_DATA",
        data: {
          fromTime: slot.start_time,
          toTime: endTime24h,
          slotCount: slot.total_count || "",
          slotDuration: slot.slot_duration || "30",
          durationUnit: "Mins",
          autoApprove:
            slot.is_autoapproved !== undefined ? slot.is_autoapproved : true,
        },
      });

      setSelectedSlot(slot);
      setEditDialogOpen(true);
    },
    [slotState.availableSlotsDate]
  );

  const handleEditSlot = useCallback(async () => {
    const validation = validateForm(
      editFormState.data,
      timeUtils.formatDate(selectedSlot.date)
    );
    if (!validation.valid) {
      editFormDispatch({ type: "SET_ERRORS", errors: validation.errors });
      return;
    }

    slotDispatch({ type: "SET_LOADING", section: "edit", value: true });

    try {
      const obj = {
        slot_id: selectedSlot.id,
        clinic_id: user.id,
        selectedDate: timeUtils.formatDate(selectedSlot.date),
        start_time: editFormState.data.fromTime,
        end_time: editFormState.data.toTime,
        total_count: editFormState.data.slotCount,
        slotDuration: editFormState.data.slotDuration,
        is_autoapproved: editFormState.data.autoApprove,
      };

      await api.put("v1/clinic/update/slots", obj);
      showSnackbar("Slot updated successfully");
      setEditDialogOpen(false);
      await fetchSlots(slotState.availableSlotsDate, false);
    } catch (err) {
      const errorMessage = en_ja["Error updating slot"];
      showSnackbar(errorMessage, "error");
    } finally {
      slotDispatch({ type: "SET_LOADING", section: "edit", value: false });
    }
  }, [
    editFormState.data,
    selectedSlot,
    user.id,
    showSnackbar,
    fetchSlots,
    slotState.availableSlotsDate,
    validateForm,
  ]);

  const goToToday = useCallback(() => {
    slotDispatch({ type: "SET_SELECTED_DATE", date: todayDate });
    setSelectedCalendarDate(null);
    slotDispatch({
      type: "SET_DATES",
      availableDate: todayDate,
      historyDate: slotState.selectedHistoryDate,
    });
    fetchSlots(todayDate, false);
  }, [todayDate, fetchSlots, slotState.selectedHistoryDate]);

  const handleCloseSnackbar = useCallback(() => {
    setSnackbarOpen(false);
    setSnackbarMessage("");
    setSnackbarType("success");
  }, []);

  const SlotCard = React.memo(({ slot, onClick, isHistory = false }) => {
    const isAvailable = slot.is_available;
    const isCompleted = slot.available_count === "0";
    const bookedSlotCount =
      parseInt(slot.total_count) - parseInt(slot.available_count);

    const getStatusColor = () => {
      if (isHistory) {
        if (isCompleted) return "#8b5cf6";
        if (isAvailable) return "#10b981";
        return "#f59e0b";
      }
      if (isCompleted) return "#6c757d";
      if (isAvailable) return "#28a745";
      return "#dc3545";
    };

    const getStatusBgColor = () => {
      if (isHistory) return "#fafafb";
      if (isCompleted) return "#f8f9fa";
      if (isAvailable) return "#f8fff9";
      return "#fff5f5";
    };

    const getStatusIcon = () => {
      if (isCompleted) return <CheckCircle sx={{ fontSize: 14 }} />;
      return <Circle sx={{ fontSize: 14 }} />;
    };

    const canClick =
      !isHistory && timeUtils.canEditSlot(slot, slotState.availableSlotsDate);

    const getDayCellClassNames = useCallback((arg) => {
      const cellDate = timeUtils.formatDate(arg.date);
      const today = todayDate;

      // Don't add selected class if it's today (today styling takes priority)
      if (selectedCalendarDate &&
        isSameDate(cellDate, selectedCalendarDate) &&
        !isSameDate(cellDate, today)) {
        return ['fc-day-selected-custom'];
      }
      return [];
    }, [selectedCalendarDate, todayDate]);

    return (
      <Card
        onClick={() => canClick && onClick(slot)}
        sx={{
          minWidth: 110,
          maxWidth: 130,
          cursor: canClick ? "pointer" : "default",
          transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
          border: isHistory ? "1px solid #d1d5db" : "1px solid #e9ecef",
          borderRadius: 2,
          backgroundColor: getStatusBgColor(),
          position: "relative",
          overflow: "hidden",
          boxShadow: isHistory
            ? "0 1px 3px rgba(0,0,0,0.1)"
            : "0 1px 3px rgba(0,0,0,0.12), 0 1px 2px rgba(0,0,0,0.24)",
          opacity: isHistory ? 0.85 : 1,
          filter: isHistory ? "grayscale(20%)" : "none",
          "&:hover": canClick
            ? {
              transform: "translateY(-2px)",
              boxShadow:
                "0 4px 8px rgba(0,0,0,0.15), 0 2px 4px rgba(0,0,0,0.15)",
              borderColor: getStatusColor(),
              backgroundColor: "#fff",
            }
            : {},
          "&:active": canClick
            ? {
              transform: "translateY(0px)",
              boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
            }
            : {},
        }}
      >
        <CardContent sx={{ p: 1.5, "&:last-child": { pb: 1.5 } }}>
          <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
            <Box sx={{ color: getStatusColor(), mr: 0.5 }}>
              {getStatusIcon()}
            </Box>
            <Typography
              variant="caption"
              fontWeight="600"
              color={isHistory ? "#374151" : "#2c3e50"}
              sx={{ fontSize: "0.75rem" }}
            >
              {slot.start_time}
            </Typography>
            <Chip
              label={`${slot.slot_duration || 30}m`}
              size="small"
              sx={{
                ml: 0.5,
                height: "16px",
                fontSize: "0.6rem",
                fontWeight: "600",
                backgroundColor: isHistory ? "#e5e7eb" : "#f3f4f6",
                color: isHistory ? "#6b7280" : "#374151",
                "& .MuiChip-label": { px: 0.5 },
              }}
            />
          </Box>
          {isHistory ? (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexDirection: "column",
                mt: 1,
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", mb: 0.5 }}>
                <Person sx={{ fontSize: 14, mr: 0.5, color: "#6b7280" }} />
                <Typography
                  variant="h6"
                  color="#374151"
                  sx={{ fontSize: "1.1rem", fontWeight: "700", lineHeight: 1 }}
                >
                  {bookedSlotCount}/{slot.total_count}
                </Typography>
              </Box>
              <Typography
                variant="caption"
                color="#6b7280"
                sx={{
                  fontSize: "0.65rem",
                  textTransform: "uppercase",
                  letterSpacing: 0.5,
                  fontWeight: "500",
                }}
              >
                {en_ja["Slots Booked"]}
              </Typography>
            </Box>
          ) : (
            <>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  mb: 0.5,
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <Person sx={{ fontSize: 12, mr: 0.5, color: "#6c757d" }} />
                  <Typography
                    variant="caption"
                    color="#6c757d"
                    sx={{ fontSize: "0.7rem" }}
                  >
                    {bookedSlotCount}/{slot.total_count}
                  </Typography>
                </Box>
              </Box>
              <Typography
                variant="caption"
                sx={{
                  display: "block",
                  color: getStatusColor(),
                  fontWeight: "600",
                  fontSize: "0.65rem",
                  textTransform: "uppercase",
                  letterSpacing: 0.5,
                  textAlign: "center",
                  py: 0.3,
                  backgroundColor: isCompleted
                    ? "#e9ecef"
                    : isAvailable
                      ? "#d4edda"
                      : "#f8d7da",
                  borderRadius: 1,
                  mt: 0.5,
                }}
              >
                {isCompleted ? en_ja["DONE"] : isAvailable ? en_ja["OPEN"] : en_ja["FULL"]}
              </Typography>
            </>
          )}
        </CardContent>
      </Card>
    );
  });

  const NoSlotsMessage = React.memo(({ message }) => (
    <Box
      sx={{
        textAlign: "center",
        py: 4,
        color: "#666",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: 1,
      }}
    >
      <Schedule sx={{ fontSize: 48, color: "#ddd" }} />
      <Typography variant="body1" color="#999">
        {message}
      </Typography>
    </Box>
  ));

  const getDayCellClassNames = useCallback((arg) => {
    const cellDate = timeUtils.formatDate(arg.date);
    const today = todayDate;

    // Don't add selected class if it's today (today styling takes priority)
    if (selectedCalendarDate &&
      isSameDate(cellDate, selectedCalendarDate) &&
      !isSameDate(cellDate, today)) {
      return ['fc-day-selected-custom'];
    }
    return [];
  }, [selectedCalendarDate, todayDate]);

  return (
    <Box sx={{ p: 3, maxWidth: "100%" }}>
      <CustomSnackbar
        isOpen={snackbarOpen}
        message={snackbarMessage}
        onClose={handleCloseSnackbar}
        type={snackbarType}
      />
      <Box sx={{ display: "flex", gap: 2, mb: 3, alignItems: "flex-start" }}>
        <Box sx={{ width: 580, flexShrink: 0 }}>
          <Paper
            elevation={4}
            sx={{
              p: 3,
              borderRadius: "16px",
              border: "1px solid rgba(0,0,0,0.08)",
              background: "white",
              transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
              "&:hover": {
                boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
                transform: "translateY(-4px)",
                border: "1px solid rgba(220, 53, 69, 0.15)",
              },
            }}
          >
            {!timeUtils.isToday(slotState.selectedDate) && (
              <Box sx={{ mb: 2, display: "flex", justifyContent: "center" }}>
                <Button
                  variant="outlined"
                  startIcon={<Today />}
                  onClick={goToToday}
                  sx={{
                    borderColor: "#dc3545",
                    color: "#dc3545",
                    borderRadius: "20px",
                    textTransform: "none",
                    fontWeight: 600,
                    "&:hover": {
                      backgroundColor: "#dc3545",
                      color: "white",
                      borderColor: "#dc3545",
                    },
                  }}
                >
                  {en_ja["Go to Today"]}
                </Button>
              </Box>
            )}
            <Box
              sx={{
                "& .fc": {
                  fontFamily:
                    '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
                  fontSize: "0.75rem",
                  lineHeight: 1.4,
                  backgroundColor: "transparent",
                },
                "& .fc-header-toolbar": {
                  marginBottom: "16px !important",
                  padding: "12px 16px",
                  backgroundColor: "#f8fafc",
                  border: "1px solid rgba(0,0,0,0.06)",
                  borderRadius: "12px",
                  boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                },
                "& .fc-toolbar-title": {
                  fontSize: "1rem !important",
                  fontWeight: "700 !important",
                  color: "#1a1a1a",
                  letterSpacing: "-0.02em",
                  margin: "0",
                },
                "& .fc-button": {
                  backgroundColor: "white !important",
                  border: "1px solid rgba(0,0,0,0.08) !important",
                  color: "#374151 !important",
                  padding: "8px !important",
                  fontSize: "16px !important",
                  fontWeight: "600 !important",
                  borderRadius: "10px !important",
                  minHeight: "36px !important",
                  minWidth: "36px !important",
                  width: "36px !important",
                  height: "36px !important",
                  display: "flex !important",
                  alignItems: "center !important",
                  justifyContent: "center !important",
                  transition:
                    "all 0.2s cubic-bezier(0.4, 0, 0.2, 1) !important",
                  boxShadow: "0 2px 6px rgba(0,0,0,0.06) !important",
                  "&:hover": {
                    backgroundColor: "#dc3545 !important",
                    color: "white !important",
                    transform: "translateY(-2px) scale(1.05) !important",
                    boxShadow: "0 6px 16px rgba(220, 53, 69, 0.3) !important",
                    border: "1px solid #dc3545 !important",
                  },
                  "&:focus": {
                    boxShadow: "0 0 0 3px rgba(220, 53, 69, 0.1) !important",
                    outline: "none !important",
                  },
                  "&:active": {
                    transform: "translateY(0) scale(0.98) !important",
                  },
                },
                "& .fc-daygrid": {
                  backgroundColor: "white",
                  borderRadius: "12px",
                  padding: "16px",
                  border: "1px solid rgba(0,0,0,0.06)",
                  boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
                },
                "& .fc-col-header": {
                  backgroundColor: "#f8fafc",
                  borderBottom: "1px solid rgba(0,0,0,0.06)",
                  padding: "12px 0",
                  borderRadius: "8px 8px 0 0",
                  marginBottom: "8px",
                },
                "& .fc-col-header-cell": {
                  border: "none",
                  padding: "0 !important",
                },
                "& .fc-col-header-cell-cushion": {
                  color: "#6b7280",
                  fontWeight: "700 !important",
                  fontSize: "0.65rem !important",
                  textTransform: "uppercase",
                  letterSpacing: "1px",
                  padding: "0",
                  textDecoration: "none",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  height: "100%",
                },
                "& .fc-daygrid-day": {
                  border: "none !important",
                  height: "32px !important",
                  minHeight: "32px !important",
                  position: "relative",
                  padding: "2px",
                },
                "& .fc-daygrid-day-frame": {
                  cursor: "pointer",
                  transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                  minHeight: "28px !important",
                  height: "28px !important",
                  display: "flex !important",
                  alignItems: "center !important",
                  justifyContent: "center !important",
                  borderRadius: "10px",
                  margin: "1px",
                  "&:hover": {
                    backgroundColor: "#f1f5f9 !important",
                    transform: "scale(1.1)",
                    boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                    border: "1px solid rgba(220, 53, 69, 0.2)",
                  },
                },
                "& .fc-daygrid-day-number": {
                  color: "#374151",
                  fontSize: "0.8rem !important",
                  fontWeight: "600 !important",
                  padding: "0 !important",
                  textDecoration: "none",
                  width: "26px",
                  height: "26px",
                  display: "flex !important",
                  alignItems: "center !important",
                  justifyContent: "center !important",
                  borderRadius: "8px",
                  transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                },
                "& .fc-day-today": {
                  backgroundColor: "transparent !important",
                  "& .fc-daygrid-day-frame": {
                    backgroundColor: "#fef2f2 !important",
                    border: "2px solid #fecaca !important",
                    boxShadow: "0 4px 12px rgba(220, 53, 69, 0.15) !important",
                    "&:hover": {
                      backgroundColor: "#fee2e2 !important",
                      border: "2px solid #fca5a5 !important",
                      boxShadow:
                        "0 6px 20px rgba(220, 53, 69, 0.25) !important",
                    },
                  },
                  "& .fc-daygrid-day-number": {
                    color: "#dc2626 !important",
                    fontWeight: "800 !important",
                  },
                },
                "& .fc-day-other": {
                  "& .fc-daygrid-day-number": {
                    color: "#d1d5db !important",
                    "&:hover": { color: "#9ca3af !important" },
                  },
                },
                "& .fc-day-sat, & .fc-day-sun": {
                  "& .fc-daygrid-day-number": {
                    color: "#dc3545 !important",
                    fontWeight: "600 !important",
                  },
                  "& .fc-daygrid-day-frame:hover": {
                    backgroundColor: "#fef2f2 !important",
                    border: "1px solid rgba(220, 53, 69, 0.3) !important",
                  },
                },
                "& .fc-scrollgrid": { border: "none !important" },
                "& .fc-scrollgrid-section > *": { border: "none !important" },
                "& .fc-scrollgrid-section-header > *": {
                  borderBottom: "none !important",
                },
                "& .fc-scrollgrid-section-body > *": {
                  border: "none !important",
                },
              }}
            >
              <FullCalendar
                ref={calendarRef}
                plugins={[dayGridPlugin, interactionPlugin]}
                initialView="dayGridMonth"
                dateClick={handleDateClick}
                height="auto"
                headerToolbar={{ left: "prev", center: "title", right: "next" }}
                titleFormat={{ year: "numeric", month: "long" }}
                initialDate={slotState.selectedDate}
                buttonText={{ prev: "‹", next: "›" }}
                buttonIcons={false}
                fixedWeekCount={false}
                showNonCurrentDates={true}
                aspectRatio={2.1}
                // dayCellClassNames={(dateInfo) => {
                //   return dateInfo.dateStr === slotState.selectedDate
                //     ? ["fc-selected-date"]
                //     : [];
                // }}
                dayCellClassNames={getDayCellClassNames}
                key={slotState.selectedDate}
              />
            </Box>
            <style jsx global>
              {`
                .fc .fc-selected-date .fc-daygrid-day-frame {
                    background: linear-gradient(
                        135deg,
                        #dc3545 0%,
                        #c82333 100%
                    ) !important;
                    color: white !important;
                    box-shadow: 0 12px 32px rgba(220, 53, 69, 0.5) !important;
                    transform: scale(1.35) !important;
                    border: 4px solid #ffffff !important;
                    outline: 3px solid #dc3545 !important;
                    z-index: 20 !important;
                    position: relative !important;
                }
                .fc .fc-selected-date .fc-daygrid-day-number {
                    color: white !important;
                    font-weight: 900 !important;
                    font-size: 1rem !important;
                    text-shadow: 0 2px 6px rgba(0, 0, 0, 0.4) !important;
                }
                .fc .fc-selected-date .fc-daygrid-day-frame:hover {
                    background: linear-gradient(
                        135deg,
                        #c82333 0%,
                        #b91c2c 100%
                    ) !important;
                    transform: scale(1.4) !important;
                    box-shadow: 0 16px 40px rgba(220, 53, 69, 0.6) !important;
                }
                
                /* New styles for selected date (not today) */
                .fc .fc-day-selected-custom .fc-daygrid-day-frame {
                    background: linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%) !important;
                    border: 2px solid #2196f3 !important;
                    box-shadow: 0 6px 16px rgba(33, 150, 243, 0.25) !important;
                    transform: scale(1.15) !important;
                    z-index: 10 !important;
                    position: relative !important;
                }
                .fc .fc-day-selected-custom .fc-daygrid-day-number {
                    color: #1565c0 !important;
                    font-weight: 700 !important;
                    font-size: 0.9rem !important;
                }
                .fc .fc-day-selected-custom .fc-daygrid-day-frame:hover {
                    background: linear-gradient(135deg, #bbdefb 0%, #90caf9 100%) !important;
                    transform: scale(1.2) !important;
                    box-shadow: 0 8px 20px rgba(33, 150, 243, 0.35) !important;
                    border: 2px solid #1976d2 !important;
                }
            `}
            </style>
          </Paper>
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Paper
            elevation={3}
            sx={{
              p: 3,
              borderRadius: 2,
              border: "1px solid #e0e0e0",
              transition: "all 0.2s ease",
              "&:hover": {
                boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
                transform: "translateY(-2px)",
              },
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                mb: 3,
              }}
            >
              <Typography
                variant="subtitle1"
                fontWeight="600"
                color="#333"
                sx={{ fontSize: "1.1rem" }}
              >
                {en_ja["Select Time (24-hour format):"]}
              </Typography>
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <AccessTime sx={{ color: "#dc3545", mr: 0.5, fontSize: 16 }} />
                <Typography
                  variant="body2"
                  fontWeight="600"
                  color="#dc3545"
                  sx={{ fontSize: "0.9rem" }}
                >
                  {formatDate(slotState.availableSlotsDate)}
                </Typography>
              </Box>
            </Box>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: 3,
                mb: 3,
              }}
            >
              <Box>
                <Typography
                  variant="body2"
                  fontWeight="600"
                  color="#555"
                  sx={{ mb: 1.5 }}
                >
                  {en_ja["From:"]}
                </Typography>
                <TextField
                  type="time"
                  value={formState.data.fromTime}
                  onChange={(e) => handleFormChange("fromTime", e.target.value)}
                  size="small"
                  fullWidth
                  error={!!formState.errors.fromTime}
                  sx={{
                    "& .MuiOutlinedInput-root": { backgroundColor: "#f8f9fa" },
                  }}
                />
                {formState.errors.fromTime && (
                  <Typography
                    variant="caption"
                    color="error"
                    sx={{ mt: 0.5, display: "block" }}
                  >
                    {formState.errors.fromTime}
                  </Typography>
                )}
              </Box>
              <Box>
                <Typography
                  variant="body2"
                  fontWeight="600"
                  color="#555"
                  sx={{ mb: 1.5 }}
                >
                  {en_ja["To:"]}
                </Typography>
                <TextField
                  type="time"
                  value={formState.data.toTime}
                  onChange={(e) => handleFormChange("toTime", e.target.value)}
                  size="small"
                  fullWidth
                  error={!!formState.errors.toTime}
                  sx={{
                    "& .MuiOutlinedInput-root": { backgroundColor: "#f8f9fa" },
                  }}
                />
                {formState.errors.toTime && (
                  <Typography
                    variant="caption"
                    color="error"
                    sx={{ mt: 0.5, display: "block" }}
                  >
                    {formState.errors.toTime}
                  </Typography>
                )}
              </Box>
              <Box>
                <Typography
                  variant="body2"
                  fontWeight="600"
                  color="#555"
                  sx={{ mb: 1.5 }}
                >
                  {en_ja["Slot count:"]}
                </Typography>
                <TextField
                  value={formState.data.slotCount}
                  onChange={(e) =>
                    handleFormChange("slotCount", e.target.value)
                  }
                  size="small"
                  fullWidth
                  error={!!formState.errors.slotCount}
                  type="number"
                  placeholder="04"
                  sx={{
                    "& .MuiOutlinedInput-root": { backgroundColor: "#f8f9fa" },
                  }}
                />
                {formState.errors.slotCount && (
                  <Typography
                    variant="caption"
                    color="error"
                    sx={{ mt: 0.5, display: "block" }}
                  >
                    {formState.errors.slotCount}
                  </Typography>
                )}
              </Box>
              <Box>
                <Typography
                  variant="body2"
                  fontWeight="600"
                  color="#555"
                  sx={{ mb: 1.5 }}
                >
                  {en_ja["Slot Duration (minutes):"]}
                </Typography>
                <TextField
                  value={formState.data.slotDuration}
                  onChange={(e) =>
                    handleFormChange("slotDuration", e.target.value)
                  }
                  size="small"
                  fullWidth
                  error={!!formState.errors.slotDuration}
                  type="number"
                  placeholder="30"
                  sx={{
                    "& .MuiOutlinedInput-root": { backgroundColor: "#f8f9fa" },
                  }}
                />
                {formState.errors.slotDuration && (
                  <Typography
                    variant="caption"
                    color="error"
                    sx={{ mt: 0.5, display: "block" }}
                  >
                    {formState.errors.slotDuration}
                  </Typography>
                )}
              </Box>
            </Box>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                pt: 1,
              }}
            >
              <FormControlLabel
                control={
                  <Switch
                    checked={formState.data.autoApprove}
                    onChange={(e) =>
                      handleFormChange("autoApprove", e.target.checked)
                    }
                    sx={{
                      "& .MuiSwitch-switchBase.Mui-checked": {
                        color: "#28a745",
                      },
                      "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track":
                        { backgroundColor: "#28a745" },
                    }}
                  />
                }
                label={
                  <Typography variant="body2" fontWeight="500" color="#555">
                    {en_ja['Auto Approve slot:']}
                  </Typography>
                }
              />
              <Button
                variant="contained"
                onClick={handleSave}
                disabled={slotState.loading.create}
                sx={{
                  backgroundColor: "#dc3545",
                  color: "white",
                  borderRadius: 1.5,
                  textTransform: "none",
                  fontWeight: "600",
                  px: 4,
                  py: 1,
                  fontSize: "0.875rem",
                  boxShadow: "0 2px 8px rgba(220, 53, 69, 0.3)",
                  "&:hover": {
                    backgroundColor: "#c82333",
                    boxShadow: "0 4px 12px rgba(220, 53, 69, 0.4)",
                    transform: "translateY(-1px)",
                  },
                }}
              >
                {slotState.loading.create ? en_ja["Saving..."] : en_ja["Save"]}
              </Button>
            </Box>
          </Paper>
        </Box>
      </Box>
      <Paper
        elevation={3}
        sx={{
          p: 3,
          borderRadius: 2,
          border: "1px solid #e0e0e0",
          mb: 3,
          position: "relative",
          transition: "all 0.2s ease",
          "&:hover": {
            boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
            transform: "translateY(-2px)",
          },
        }}
      >
        {slotState.loading.available && (
          <LinearProgress
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              height: "3px",
              borderTopLeftRadius: "8px",
              borderTopRightRadius: "8px",
            }}
          />
        )}
        <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
          <EventAvailable sx={{ color: "#dc3545", mr: 1.5, fontSize: 24 }} />
          <Typography variant="h6" fontWeight="600" color="#333">
            {en_ja["Available Slots:"]}{" "}
            {formatDate(slotState.availableSlotsDate)}
          </Typography>
        </Box>
        {slotState.noSlots.available ? (
          <NoSlotsMessage message="No slots created for this date" />
        ) : (
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              gap: 1.2,
              justifyContent: "flex-start",
              "& > *": { flexShrink: 0 },
            }}
          >
            {slotState.slots.available?.map((slot) => (
              <SlotCard key={slot.id} slot={slot} onClick={handleSlotClick} />
            ))}
          </Box>
        )}
      </Paper>
      <Paper
        ref={historyRef}
        elevation={3}
        sx={{
          borderRadius: 2,
          border: highlightHistory ? "2px solid #dc3545" : "1px solid #e0e0e0",
          transition: "all 0.4s ease",
          transform: highlightHistory ? "scale(1.008)" : "scale(1)",
          boxShadow: highlightHistory
            ? "0 8px 32px rgba(220, 53, 69, 0.15)"
            : "0 4px 12px rgba(0,0,0,0.08)",
          "&:hover": {
            boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
            transform: highlightHistory ? "scale(1.008)" : "translateY(-2px)",
          },
        }}
      >
        <Box sx={{ p: 3 }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 2,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <Schedule sx={{ color: "#666", mr: 1.5, fontSize: 24 }} />
              <Typography variant="h6" fontWeight="600" color="#333">
                {en_ja["History"]}
              </Typography>
            </Box>
            <IconButton
              onClick={() => setHistoryExpanded(!historyExpanded)}
              sx={{
                backgroundColor: "#f5f5f5",
                transition: "all 0.2s ease",
                "&:hover": {
                  backgroundColor: "#e0e0e0",
                  transform: "scale(1.05)",
                },
              }}
            >
              {historyExpanded ? <ExpandLess /> : <ExpandMore />}
            </IconButton>
          </Box>
          <Collapse in={historyExpanded}>
            <Fade in={historyExpanded}>
              <Box>
                <Typography variant="body2" color="#666" sx={{ mb: 3 }}>
                  {en_ja['Date:']}{" "}
                  {slotState.selectedHistoryDate
                    ? formatDate(
                      slotState.selectedHistoryDate)
                    : en_ja["No date selected"]}{" "}
                  {slotState.selectedHistoryDate && en_ja["(Past)"]}
                </Typography>
                {slotState.loading.history ? (
                  <Box
                    sx={{ display: "flex", justifyContent: "center", py: 2 }}
                  >
                    <LinearProgress sx={{ width: "50%" }} />
                  </Box>
                ) : slotState.noSlots.history ? (
                  <NoSlotsMessage message="No slots found for this date" />
                ) : slotState.selectedHistoryDate ? (
                  <Box
                    sx={{
                      display: "flex",
                      flexWrap: "wrap",
                      gap: 1.2,
                      justifyContent: "flex-start",
                      "& > *": { flexShrink: 0 },
                    }}
                  >
                    {slotState.slots.history?.map((slot) => (
                      <SlotCard
                        key={slot.id}
                        slot={slot}
                        onClick={() => { }}
                        isHistory={true}
                      />
                    ))}
                  </Box>
                ) : (
                  <NoSlotsMessage message={en_ja["Select a past date to view history"]} />
                )}
              </Box>
            </Fade>
          </Collapse>
        </Box>
      </Paper>
      <Dialog
        open={editDialogOpen}
        onClose={() => setEditDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 3, boxShadow: "0 12px 40px rgba(0,0,0,0.15)" },
        }}
      >
        <DialogTitle
          sx={{ fontSize: "18px", fontWeight: "600", color: "#333" }}
        >
          {en_ja['Edit Slot']} - {selectedSlot ? selectedSlot.start_time : ""}
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: 2,
              mb: 2,
            }}
          >
            <Box>
              <Typography
                variant="body2"
                fontWeight="600"
                color="#555"
                sx={{ mb: 1 }}
              >
                {en_ja["From:"]}
              </Typography>
              <TextField
                type="time"
                value={editFormState.data.fromTime}
                onChange={(e) =>
                  handleEditFormChange("fromTime", e.target.value)
                }
                size="small"
                fullWidth
                error={!!editFormState.errors.fromTime}
              />
              {editFormState.errors.fromTime && (
                <Typography
                  variant="caption"
                  color="error"
                  sx={{ mt: 0.5, display: "block" }}
                >
                  {editFormState.errors.fromTime}
                </Typography>
              )}
            </Box>
            <Box>
              <Typography
                variant="body2"
                fontWeight="600"
                color="#555"
                sx={{ mb: 1 }}
              >
                {en_ja["To:"]}
              </Typography>
              <TextField
                type="time"
                value={editFormState.data.toTime}
                onChange={(e) => handleEditFormChange("toTime", e.target.value)}
                size="small"
                fullWidth
                error={!!editFormState.errors.toTime}
              />
              {editFormState.errors.toTime && (
                <Typography
                  variant="caption"
                  color="error"
                  sx={{ mt: 0.5, display: "block" }}
                >
                  {editFormState.errors.toTime}
                </Typography>
              )}
            </Box>
            <Box>
              <Typography
                variant="body2"
                fontWeight="600"
                color="#555"
                sx={{ mb: 1 }}
              >
                {en_ja["Slot count:"]}
              </Typography>
              <TextField
                value={editFormState.data.slotCount}
                onChange={(e) =>
                  handleEditFormChange("slotCount", e.target.value)
                }
                size="small"
                fullWidth
                error={!!editFormState.errors.slotCount}
                type="number"
              />
              {editFormState.errors.slotCount && (
                <Typography
                  variant="caption"
                  color="error"
                  sx={{ mt: 0.5, display: "block" }}
                >
                  {editFormState.errors.slotCount}
                </Typography>
              )}
            </Box>
            <Box>
              <Typography
                variant="body2"
                fontWeight="600"
                color="#555"
                sx={{ mb: 1 }}
              >
                {en_ja["Slot Duration (minutes):"]}
              </Typography>
              <TextField
                value={editFormState.data.slotDuration}
                onChange={(e) =>
                  handleEditFormChange("slotDuration", e.target.value)
                }
                size="small"
                fullWidth
                error={!!editFormState.errors.slotDuration}
                type="number"
              />
              {editFormState.errors.slotDuration && (
                <Typography
                  variant="caption"
                  color="error"
                  sx={{ mt: 0.5, display: "block" }}
                >
                  {editFormState.errors.slotDuration}
                </Typography>
              )}
            </Box>
          </Box>
          <FormControlLabel
            control={
              <Switch
                checked={editFormState.data.autoApprove}
                onChange={(e) =>
                  handleEditFormChange("autoApprove", e.target.checked)
                }
                sx={{
                  "& .MuiSwitch-switchBase.Mui-checked": { color: "#28a745" },
                  "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                    backgroundColor: "#28a745",
                  },
                }}
              />
            }
            label={
              <Typography variant="body2" fontWeight="500" color="#555">
                {en_ja["Auto Approve slot"]}:
              </Typography>
            }
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions sx={{ p: 3, gap: 1 }}>
          <Button
            onClick={() => setEditDialogOpen(false)}
            startIcon={<Cancel />}
            sx={{ textTransform: "none", borderRadius: 2 }}
          >
            {en_ja["Cancel"]}
          </Button>
          <Button
            onClick={handleEditSlot}
            variant="contained"
            startIcon={<Save />}
            disabled={slotState.loading.edit}
            sx={{
              backgroundColor: "#2196f3",
              textTransform: "none",
              borderRadius: 2,
              "&:hover": {
                backgroundColor: "#1976d2",
                transform: "translateY(-1px)",
              },
            }}
          >
            {slotState.loading.edit ? en_ja["Updating..."] : en_ja["Save Changes"]}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AppointmentSlots;
